import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import type { PaycodeCombination } from 'repository/timekeeper/@types/PaycodeCombination'
import { editPycodesCombination } from 'utils/PaycodesCombination'
import { PaycodesCombinationFormDialog, type PaycodesCombinationSubmitPayload } from './PaycodesCombinationFormDialog'
import { usePaycodesOptions } from 'hooks/paycodesCombinations/usePaycodesOptions'

interface Props {
    open: boolean
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
    selectedRow: PaycodeCombination[]
    setSelected: () => void
    reloading: () => void
    rows: PaycodeCombination[]
}

const EditPaycodesCombination = ({ open, setOpen, selectedRow, setSelected, reloading, rows }: Props) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const [loadingEdit, setLoadingEdit] = useState(false)

    const selected = selectedRow?.[0]
    const { paycodes, loadingPaycodes } = usePaycodesOptions({ enabled: open && Boolean(selected), reloadKey: selected?.id })

    const handleClose = () => {
        setOpen(false)
        setSelected()
    }

    const handleEdit = (data: PaycodesCombinationSubmitPayload) => {
        if (!selected) {
            return
        }

        setLoadingEdit(true)
        editPycodesCombination(setLoadingEdit, selected.id, data)
            .then((response: { status: number }) => {
                if (response.status === 200) {
                    enqueueSnackbar(t('settings.paycodes_combination.dialog.alerts.edit.success'), { variant: 'success' })
                    reloading()
                    handleClose()
                }
            })
            .catch((error: unknown) => {
                console.error(error)
                enqueueSnackbar(t('settings.paycodes_combination.dialog.alerts.edit.error'), { variant: 'error' })
            })
            .finally(() => {
                setLoadingEdit(false)
            })
    }

    return (
        <PaycodesCombinationFormDialog
            open={open}
            onClose={handleClose}
            onSubmit={handleEdit}
            title={t('settings.paycodes_combination.dialog.titles.edit')}
            saveButtonText={t('general.buttons.edit')}
            rows={rows}
            paycodesOptions={paycodes}
            loadingPaycodes={loadingPaycodes}
            isSaving={loadingEdit}
            initialValues={{
                name: selected?.name,
                description: selected?.description,
                start_date: selected?.start_date,
                end_date: selected?.end_date,
                paycodes: selected?.paycodes || [],
            }}
            requiredPermissions={['timekeeper.GetPaycodes', 'timekeeper.GetPaycodesCombinations']}
            descriptionRows={4}
        />
    )
}

export { EditPaycodesCombination }
